import * as THREE from 'three';
import openModalFitHeight from '../../../../components/common/Modal/openModalConfirmFitHeight';
import { openMessageSuccess } from '../../../../components/common/Message/openMessageSuccess';
import i18n from '../../../../languages/i18n';
import {
  getCeilingModuleList,
  getUpperModuleList,
} from '../../../../utils/modules/ModuleUtils';
import {
  closeNotification,
  openNotification,
} from '../../../../components/common/Notification/openNotification';
import { Icon } from '../../../../components/icon/icon';
import { GeometryUtils } from '../../../../utils/geometry/GeometryUtils';

/**
 * AutoFitModuleController - Quản lý việc tự động điều chỉnh vị trí và kích thước các module tủ bếp
 * Xử lý fit ceiling, auto-positioning và batch operations cho các modules
 */
export default class AutoFitModuleController {
  /**
   * Khởi tạo controller với tham chiếu đến sceneManager và positionController
   * @param {Object} sceneManager - Manager chính quản lý scene và các components
   */
  constructor(sceneManager) {
    this.sceneManager = sceneManager;
  }

  /**
   * Lấy module object theo ID từ cabinetManager
   * @param {string} moduleId - UUID của module cần tìm
   * @returns {Object|undefined} Module object hoặc undefined nếu không tìm thấy
   */
  getModuleById(moduleId) {
    return this.sceneManager.cabinetManager.getModuleByGltfUuid(moduleId);
  }

  /**
   * Tự động điều chỉnh tủ để fit với trần nhà
   * - Tính toán chiều cao cần thiết từ vị trí hiện tại đến trần
   * - Xử lý riêng cho module type "upper" (trừ đi chiều cao module trần cao nhất)
   * - Áp dụng giới hạn min/max height từ moduleData
   * - Cập nhật vị trí và đánh dấu module là fitCeiling = true
   * @param {string} moduleId - UUID của module cần fit ceiling
   * @returns {Object} Module đã được fit hoặc undefined
   */
  fitCeilingCabinet(insideLoaderRef, moduleId, showNotification = true) {
    const originalModule = this.getModuleById(moduleId);
    if (!originalModule) return;

    const { wallHeight } = this.sceneManager;
    const moduleType = originalModule.moduleType;

    // Tính toán bounding box và kích thước hiện tại của module
    const box = new THREE.Box3().setFromObject(originalModule.group);
    const size = new THREE.Vector3();
    box.getSize(size);
    const center = new THREE.Vector3();
    box.getCenter(center);

    const modules = this.sceneManager.cabinetManager.currentCabinet.modules;

    const modulesUpper = getUpperModuleList(modules);
    const modulesCeiling = getCeilingModuleList(modules);

    // Tính chiều cao từ đáy module đến trần
    const heightFromBottomModuleToCeiling = wallHeight - center.y + size.y / 2;
    let newHeight = heightFromBottomModuleToCeiling;
    if (
      ['vach_lam', 'vach_guong', 'vach_tron'].some(item =>
        moduleType.includes(item)
      )
    ) {
      const height =
        originalModule.formularController?.currentDimensions?.height;

      const shortestTopIntersectionModule =
        this.getShortestToFaceIntersectionModule(
          center,
          new THREE.Vector3(center.x, center.y + size.y / 2, center.z),
          modulesUpper.concat(modulesCeiling)
        );
      if (shortestTopIntersectionModule) {
        newHeight =
          shortestTopIntersectionModule.center.y -
          shortestTopIntersectionModule.size.y / 2 -
          (center.y - size.y / 2);
      }

      newHeight = newHeight - height;
      // Áp dụng giới hạn min/max từ moduleData
      const { max, min } =
        originalModule.formularController.currentOptions.limitCaoVachLam;
      if (newHeight > max) newHeight = max;
      else if (newHeight < min) newHeight = min;
      this.scaleModule(
        insideLoaderRef,
        moduleId,
        {},
        { caoVachLam: newHeight },
        showNotification
      );
    } else {
      let shortestCeilingModule;
      let shortestUpperCabinet;

      if (!['ceiling'].some(item => moduleType.includes(item))) {
        shortestCeilingModule = this.getShortestInModules(modulesCeiling);
      }
      if (!['upper', 'ceiling'].some(item => moduleType.includes(item))) {
        shortestUpperCabinet = this.getShortestInModules(modulesUpper);
      }

      // Chiều cao cố định của các khối module trên
      const fixedHeight =
        (shortestCeilingModule?.size?.y || 0) +
        (shortestUpperCabinet?.size?.y || 0);
      if (fixedHeight > 0) {
        newHeight = newHeight - fixedHeight;
      }
      // Áp dụng giới hạn min/max từ moduleData
      const { max, min } = originalModule.moduleData?.limit?.height;
      if (newHeight > max) newHeight = max;
      else if (newHeight < min) newHeight = min;
      // Scale module với chiều cao mới
      this.scaleModule(
        insideLoaderRef,
        moduleId,
        { height: newHeight },
        {},
        showNotification
      );
    }

    // Đánh dấu module đã fit ceiling
    originalModule.setFitCeiling(true);
    originalModule.group.updateMatrixWorld(true);

    return originalModule;
  }

  getShortestToFaceIntersectionModule(centerModule, centerFace, modules) {
    let shortestIntersectionModule = null;

    modules.forEach(([key, module]) => {
      const hasIntersection = this.checkIntersectionModule(
        centerModule,
        centerFace,
        module.group
      );
      const box = new THREE.Box3().setFromObject(module.group);
      const size = new THREE.Vector3();
      box.getSize(size);
      const center = box.getCenter(new THREE.Vector3());
      if (
        hasIntersection &&
        (!shortestIntersectionModule ||
          shortestIntersectionModule.center.y > center.y)
      ) {
        shortestIntersectionModule = {
          center,
          size,
          module,
        };
      }
    });

    return shortestIntersectionModule;
  }

  /**
   * Tìm điểm giao giữa đường thẳng (từ centerModule đến centerFace) và bounding box của module khác
   * Sử dụng bounding box thay vì mesh geometry để tránh vấn đề module thiếu mặt
   * @param {THREE.Vector3} centerModule - Điểm trung tâm của module
   * @param {THREE.Vector3} centerFace - Điểm trung tâm của mặt
   * @param {THREE.Group|THREE.Object3D} targetGroup - Module/group cần kiểm tra giao
   * @returns {THREE.Vector3|null} Điểm giao hoặc null nếu không có giao điểm
   */
  checkIntersectionModule(centerModule, centerFace, targetGroup) {
    // Tạo direction vector từ centerModule đến centerFace
    const direction = new THREE.Vector3()
      .subVectors(centerFace, centerModule)
      .normalize();

    // Tạo Ray từ centerModule theo direction
    const ray = new THREE.Ray(centerModule, direction);

    // Lấy bounding box của target
    const targetBox = new THREE.Box3().setFromObject(targetGroup);

    // Tính intersection với bounding box
    const intersectionPoint = new THREE.Vector3();
    const hasIntersection = ray.intersectBox(targetBox, intersectionPoint);

    if (!hasIntersection) {
      return false;
    }
    return true;

    // // Kiểm tra intersection point có nằm về phía trước centerFace không
    // const distanceFromModule = centerModule.distanceTo(intersectionPoint);
    // const distanceModuleToFace = centerModule.distanceTo(centerFace);

    // // Intersection phải xa hơn centerFace so với centerModule
    // if (distanceFromModule > distanceModuleToFace - 0.001) {
    //   return intersectionPoint;
    // }

    // return null;
  }

  /**
   * Tìm chiều cao lớn nhất trong tất cả các module có type "ceiling"
   * @returns {number} Chiều cao max của ceiling modules hoặc 0 nếu không có
   */
  getMaxHeightCeiling() {
    let maxHightestCeilingCabinet = 0;
    const modules = this.sceneManager.cabinetManager.currentCabinet.modules;

    // Duyệt qua tất cả modules và tìm ceiling module cao nhất
    Array.from(modules).forEach(([key, module]) => {
      if (
        ['ceiling'].some(item =>
          module.group.userData.moduleType.includes(item)
        )
      ) {
        const box = new THREE.Box3().setFromObject(module.group);
        const size = new THREE.Vector3();
        box.getSize(size);
        if (maxHightestCeilingCabinet < size.y) {
          maxHightestCeilingCabinet = size.y;
        }
      }
    });
    return maxHightestCeilingCabinet;
  }

  /**
   * Tìm vị trí thấp nhất trong danh sách modules được cung cấp (thường là ceiling hoặc upper modules)
   * @returns {Object|null} Vị trí thấp nhất của ceiling modules hoặc null nếu không có
   */
  getShortestInModules(modules) {
    let shortestCeilingModule = null;

    // Duyệt qua tất cả modules và tìm ceiling module cao nhất
    modules.forEach(([key, module]) => {
      const box = new THREE.Box3().setFromObject(module.group);
      const size = new THREE.Vector3();
      box.getSize(size);
      const center = box.getCenter(new THREE.Vector3());
      if (
        !shortestCeilingModule?.center?.y ||
        shortestCeilingModule.center.y > center.y
      ) {
        shortestCeilingModule = { center, module, size };
      }
    });
    return shortestCeilingModule;
  }

  /**
   * Hủy bỏ chế độ fit ceiling, trả module về kích thước gốc
   * - Kiểm tra va chạm với trần (đối với upper module)
   * - Điều chỉnh lại chiều cao nếu cần thiết để tránh vượt quá wallHeight
   * @param {string} moduleId - UUID của module cần unfit
   * @returns {Object} Module đã được unfit hoặc undefined
   */
  unFitCeilingCabinet(insideLoaderRef, moduleId) {
    const originalModule = this.getModuleById(moduleId);
    if (!originalModule) return;
    originalModule.setFitCeiling(false);

    const { wallHeight } = this.sceneManager;
    const { height } = originalModule.moduleData;
    const moduleType = originalModule.group.userData.moduleType;

    // Lấy vị trí hiện tại của module
    const box = new THREE.Box3().setFromObject(originalModule.group);
    const center = new THREE.Vector3();
    box.getCenter(center);

    let newHeight = height;

    // Kiểm tra va chạm với trần đối với upper module
    if (['upper'].some(item => moduleType.includes(item))) {
      const maxHightestCeilingCabinet = this.getMaxHeightCeiling();
      if (center.y + height / 2 + maxHightestCeilingCabinet > wallHeight) {
        newHeight = center.y + height / 2 - wallHeight;
      }
    }

    // Áp dụng giới hạn min/max
    const { max, min } = originalModule.moduleData?.limit?.height;
    if (newHeight > max) newHeight = max;
    else if (newHeight < min) newHeight = min;

    // Scale module về kích thước mới
    this.scaleModule(insideLoaderRef, moduleId, { height: newHeight }, {});

    // Đặt fitCeiling = false cho upper module
    // if (['upper'].some(item => moduleType.includes(item)))
    originalModule.group.updateMatrixWorld(true);
    return originalModule;
  }

  /**
   * Thay đổi kích thước module theo dimensions được chỉ định
   * - Áp dụng giới hạn min/max cho chiều cao
   * - Tính toán lại bounding box và cập nhật positionController
   * @param {string} moduleId - UUID của module cần scale
   * @param {Object} mdDimensions - Kích thước mới {width, height, depth}
   * @param {Object} mdOptions - Các tùy chọn scale
   * @returns {Object} Module đã được scale
   */
  // async scaleModule(
  //   insideLoaderRef,
  //   moduleId,
  //   mdDimensions,
  //   mdOptions,
  //   showNotification = true
  // ) {
  //   const originalModule = this.getModuleById(moduleId);
  //   if (originalModule) {
  //     // Lưu rotation hiện tại

  //     const oldDimensions = originalModule.formularController.currentDimensions;

  //     // Thực hiện scale module
  //     originalModule.scaleModuleWithFormular(mdDimensions, mdOptions);

  //     //Cập nhật lại vị trí module
  //     this.sceneManager.updateModulePositionAfterScale(
  //       originalModule.group,
  //       oldDimensions,
  //       mdDimensions
  //     );

  //     //Thực hiện scale inside của module
  //     if (insideLoaderRef?.current) {
  //       await insideLoaderRef?.current.scaleInsideAfterModuleScale(
  //         originalModule,
  //         oldDimensions,
  //         originalModule.formularController.currentDimensions
  //       );
  //     }

  //     this.sceneManager?.updateAllCountertopAndBacksplash();

  //     const modules = this.sceneManager.cabinetManager.currentCabinet.modules

  //     const modulesUpper = getUpperModuleList(modules);
  //     const modulesCeiling = getCeilingModuleList(modules);
  //     if (
  //       mdDimensions?.height &&
  //       ['upper', 'lower'].some(item =>
  //         originalModule.moduleType.includes(item)
  //       ) &&
  //       !['totl_lower', 'cabinet_dry_lower', 'vach_lam'].some(item =>
  //         originalModule.moduleType.includes(item)
  //       ) &&
  //       (modulesUpper.length > 0 || modulesCeiling.length > 0) &&
  //       showNotification
  //     ) {
  //       setTimeout(() => {
  //         openModalFitHeight({
  //           func: () => {
  //             if (
  //               ['lower'].some(item => originalModule.moduleType.includes(item))
  //             ) {
  //               this.moveUpperFollowHeightestLowerCabinet({
  //                 insideLoaderRef,
  //               });
  //             }
  //             if (
  //               ['upper', 'lower'].some(item =>
  //                 originalModule.moduleType.includes(item)
  //               )
  //             ) {
  //               this.moveCeilingFollowHeightestUpperCabinet({
  //                 insideLoaderRef,
  //               });
  //             }
  //           },
  //         });
  //       }, 100);
  //     }
  //   }
  //   return originalModule;
  // }

  validateHauThickness = (group, hauThickness) => {
    const listHAU = [];
    group.traverse(child => {
      if (child.name.includes('HAU')) {
        listHAU.push(child);
      }
    });
    let flag = false;
    listHAU.forEach(hau => {
      const texturePrice = hau.userData?.texturePrice;
      if (texturePrice && !texturePrice[hauThickness]) {
        flag = true;
      }
    });
    if (flag) {
      closeNotification('texture-tam-hau-error');
      openNotification({
        icon: <Icon.ICWarning />,
        key: 'texture-tam-hau-error',
        title: i18n.t(
          'The board thickness cannot be changed because the current board type does not support it!'
        ),
        description: i18n.t(
          'Please select a new color code, this color code does not support a thickness of {{x}}mm.',
          { x: hauThickness * 1000 }
        ),
        duration: 2,
      });
      return false;
    }
    return true;
  };

  async scaleModule(
    insideLoaderRef,
    moduleId,
    mdDimensions,
    mdOptions,
    showNotification = true
  ) {
    const originalModule = this.getModuleById(moduleId);

    if (originalModule) {
      //Giá trị trước thay đổi
      let oldDimensions = originalModule.formularController.currentDimensions;
      let oldOptions = originalModule.formularController.currentOptions;
      if (originalModule.moduleType.includes('corner')) {
        oldDimensions = {
          ...oldDimensions,
          depth: mdDimensions?.depth
            ? originalModule.formularController.currentDimensions.depth
            : originalModule.formularController.currentOptions.rongK1,
          width: originalModule.formularController.currentOptions.rongK2,
        };
      }

      if (
        (mdOptions?.DDTHau &&
          this.validateHauThickness(
            originalModule?.group,
            mdOptions?.DDTHau
          )) ||
        mdOptions?.DDTHau == null
      ) {
        // Thực hiện scale module
        originalModule.scaleModuleWithFormular(mdDimensions, mdOptions);

        //Giá trị sau thay đổi
        let newDimensions = originalModule.formularController.currentDimensions;
        let newOptions = originalModule.formularController.currentOptions;
        if (originalModule.moduleType.includes('corner')) {
          newDimensions = {
            ...newDimensions,
            depth: mdDimensions?.depth
              ? originalModule.formularController.currentDimensions.depth
              : originalModule.formularController.currentOptions.rongK1,
            width: originalModule.formularController.currentOptions.rongK2,
          };
        }

        //Cập nhật lại vị trí module
        this.sceneManager.updateModulePositionAfterScale(
          originalModule.group,
          oldDimensions,
          newDimensions
        );

        //Thực hiện scale inside của module
        if (insideLoaderRef?.current) {
          await insideLoaderRef?.current.scaleInsideAfterModuleScale(
            originalModule,
            oldDimensions,
            newDimensions,
            oldOptions,
            newOptions
          );
        }

        await this.sceneManager?.updateAllCountertopAndBacksplash();

        const modules = this.sceneManager.cabinetManager.currentCabinet.modules;

        const modulesUpper = getUpperModuleList(modules);
        const modulesCeiling = getCeilingModuleList(modules);
        if (
          mdDimensions?.height &&
          ['upper', 'lower'].some(item =>
            originalModule.moduleType.includes(item)
          ) &&
          !['totl_lower', 'cabinet_dry_lower', 'vach_lam'].some(item =>
            originalModule.moduleType.includes(item)
          ) &&
          (modulesUpper.length > 0 || modulesCeiling.length > 0) &&
          showNotification
        ) {
          setTimeout(() => {
            openModalFitHeight({
              func: () => {
                if (
                  ['lower'].some(item =>
                    originalModule.moduleType.includes(item)
                  )
                ) {
                  this.moveUpperFollowHeightestLowerCabinet({
                    insideLoaderRef,
                  });
                }
                if (
                  ['upper', 'lower'].some(item =>
                    originalModule.moduleType.includes(item)
                  )
                ) {
                  this.moveCeilingFollowHeightestUpperCabinet({
                    insideLoaderRef,
                  });
                }
              },
            });
          }, 100);
        }
      } else return;
    }
    return originalModule;
  }

  /**
   * Tự động di chuyển tất cả tủ trên (upper) theo chiều cao của tủ dưới cao nhất
   * - Tính toán vị trí Y mới = chiều cao tủ dưới + 0.62 (khoảng cách chuẩn)
   * - Kiểm tra va chạm với trần và điều chỉnh chiều cao nếu cần
   * - Xử lý fit ceiling cho các module đã được đánh dấu
   */
  moveUpperFollowHeightestLowerCabinet({ insideLoaderRef }) {
    const { wallHeight, countertop, backsplash, cabinetType } =
      this.sceneManager;
    let maxHightestLowerCabinet = 0;
    let maxMinHeightLimitCeilingCabinet = 0;
    const modules = this.sceneManager.cabinetManager.currentCabinet.modules;

    const moduleLowers = Array.from(modules).filter(([key, module]) =>
      [
        'lower',
        'totl_lower',
        'cabinet_dry_lower',
        'vach_lam',
        'vach_tron',
        'vach_guong',
      ].some(item => module.moduleType.includes(item))
    );

    // Tìm chiều cao lớn nhất của tủ dưới và tủ trần
    Array.from(modules).forEach(([key, module]) => {
      const height = module.formularController?.currentDimensions?.height;

      if (
        ['lower'].some(item =>
          module.group.userData.moduleType.includes(item)
        ) &&
        ![
          'totl_lower',
          'cabinet_dry_lower',
          'vach_lam',
          'vach_tron',
          'vach_guong',
        ].some(item => module.group.userData.moduleType.includes(item))
      ) {
        if (maxHightestLowerCabinet < height) {
          maxHightestLowerCabinet = height;
        }
      } else if (
        ['ceiling'].some(item =>
          module.group.userData.moduleType.includes(item)
        )
      ) {
        const { min } = module.moduleData?.limit?.height;
        if (maxMinHeightLimitCeilingCabinet < min) {
          maxMinHeightLimitCeilingCabinet = min;
        }
      }
    });

    // Tính vị trí Y mới cho tủ trên (chiều cao tủ dưới + khoảng cách 0.62)
    let newY = maxHightestLowerCabinet;
    if (cabinetType === 'kitchen') {
      newY +=
        (countertop?.dimension?.height ?? 0.02) +
        (backsplash?.dimension?.height ?? 0.6);
    }
    let newHeight = 0;

    // Điều chỉnh vị trí và kích thước tất cả tủ trên
    Array.from(modules).forEach(([key, module]) => {
      if (
        ['upper'].some(item => module.group.userData.moduleType.includes(item))
      ) {
        const box = new THREE.Box3().setFromObject(module.group);
        const size = new THREE.Vector3();
        box.getSize(size);
        const center = new THREE.Vector3();
        box.getCenter(center);
        const shortestBottomIntersectionModule =
          this.getShortestToFaceIntersectionModule(
            center,
            new THREE.Vector3(center.x, center.y - size.y / 2, center.z),
            moduleLowers
          );
        if (
          module.position.y + (shortestBottomIntersectionModule?.size?.y ?? 0) >
          newY
        ) {
          return;
        }
        if (module.fitCeiling)
          // Xử lý fit ceiling nếu module đã được đánh dấu
          this.fitCeilingCabinet(insideLoaderRef, module.group.uuid, false);

        const { x, z } = module.group.position;

        const height = module.formularController?.currentDimensions?.height;

        // Kiểm tra va chạm với trần và điều chỉnh chiều cao nếu cần
        if (newY + height + maxMinHeightLimitCeilingCabinet > wallHeight) {
          newHeight = wallHeight - newY - maxMinHeightLimitCeilingCabinet;
          const { min, max } = module.moduleData?.limit?.height;
          if (newHeight < min) {
            newHeight = min;
          } else if (newHeight > max) {
            newHeight = max;
          }

          this.scaleModule(
            insideLoaderRef,
            module.group.uuid,
            {
              height: newHeight,
            },
            undefined,
            false
          );
        }

        // Cập nhật vị trí mới
        module.group.position.set(x, newY, z);
        module.group.updateMatrixWorld(true);

        // Chuẩn bị data để cập nhật position controller
        const newPositionModel = { x, y: newY, z };
        if (newHeight > 0) {
          newPositionModel.height = newHeight;
        }
      }
    });
  }

  /**
   * Tự động di chuyển tất cả module trần (ceiling) theo vị trí cao nhất của tủ trên
   * - Tính maxHightestUpperCabinet (vị trí cao nhất của upper modules)
   * - Đặt ceiling modules ngay tại vị trí này
   * - Kiểm tra va chạm với trần và scale lại nếu vượt quá wallHeight
   */
  moveCeilingFollowHeightestUpperCabinet({ insideLoaderRef }) {
    const { wallHeight, countertop, backsplash, cabinetType } =
      this.sceneManager;
    let heightestUpperCabinet;
    let heightestLowerCabinet = 0;
    const modules = Array.from(
      this.sceneManager.cabinetManager.currentCabinet.modules
    );

    // Tìm vị trí cao nhất của các tủ trên
    modules.forEach(([key, module]) => {
      const height = module.formularController?.currentDimensions?.height;

      if (
        ['lower'].some(item => module.moduleType.includes(item)) &&
        !['totl_lower', 'cabinet_dry_lower', 'vach_lam'].some(item =>
          module.moduleType.includes(item)
        )
      ) {
        if (heightestLowerCabinet < height) {
          heightestLowerCabinet = height;
        }
      }
      if (['upper'].some(item => module.moduleType.includes(item))) {
        const box = new THREE.Box3().setFromObject(module.group);
        const size = new THREE.Vector3();
        box.getSize(size);
        const center = box.getCenter(new THREE.Vector3());
        if (!heightestUpperCabinet || heightestUpperCabinet.size.y < size.y) {
          heightestUpperCabinet = { size, module, center };
        }
      }
    });
    // heightestUpperCabinet = this.getShortestInModules(modulesUpper);

    let newY =
      (heightestUpperCabinet?.center?.y ?? 0) +
        (heightestUpperCabinet?.size?.y / 2 ?? 0) || heightestLowerCabinet;
    if (cabinetType === 'kitchen' && !heightestUpperCabinet) {
      newY +=
        (countertop?.dimension?.height ?? 0.02) +
        (backsplash?.dimension?.height ?? 0.6) +
        0.07;
    }
    const newHeight = 0;

    let needMoveUpper = false;
    // Điều chỉnh vị trí và kích thước tất cả module trần
    Array.from(modules).forEach(([key, module]) => {
      if (
        ['ceiling'].some(item =>
          module.group.userData.moduleType.includes(item)
        )
      ) {
        // Xử lý fit ceiling nếu module đã được đánh dấu
        if (module.fitCeiling)
          this.fitCeilingCabinet(insideLoaderRef, module.group.uuid);

        const { x, z } = module.group.position;

        const height = module.formularController?.currentDimensions?.height;

        const { max, min } = module.moduleData?.limit?.height;
        let tempNewY = newY;
        // Kiểm tra va chạm với trần và điều chỉnh chiều cao
        if (newY + height > wallHeight) {
          if (wallHeight - newY < min) {
            this.scaleModule(
              insideLoaderRef,
              module.group.uuid,
              {
                height: min,
              },
              undefined,
              false
            );
            tempNewY = wallHeight - min;
          } else if (wallHeight - newY > max) {
            this.scaleModule(
              insideLoaderRef,
              module.group.uuid,
              {
                height: max,
              },
              undefined,
              false
            );
            tempNewY = wallHeight - max;
          }
          needMoveUpper = true;
        }

        // Cập nhật vị trí mới
        module.group.position.set(x, tempNewY, z);
        module.group.updateMatrixWorld(true);

        // Chuẩn bị data để cập nhật position controller
        const newPositionModel = { x, y: tempNewY, z };
        if (newHeight > 0) {
          newPositionModel.height = newHeight;
        }
      }
    });
    if (needMoveUpper) {
      this.moveUpperFollowHeightestLowerCabinet({ insideLoaderRef });
    }
  }

  /**
   * Áp dụng cùng một thao tác cho tất cả modules cùng loại
   * - Loại trừ module được chọn khỏi việc áp dụng
   * - Với upper/ceiling: áp dụng fit/unfit ceiling tùy theo allowFit
   * - Với các loại khác: áp dụng scaling với mdDimensions và mdOptions
   * @param {Object} params - Tham số cấu hình
   * @param {string} params.selectedModelId - ID của module được chọn (sẽ bị loại trừ)
   * @param {string} params.selectedModelType - Loại module cần áp dụng
   * @param {boolean} params.allowFit - Cho phép fit ceiling hay không
   * @param {Object} params.mdDimensions - Kích thước mới để scale
   * @param {Object} params.mdOptions - Tùy chọn scale
   */
  applyToAllCabinets({
    insideLoaderRef,
    selectedModelId,
    allowFit = undefined,
    mdDimensions = {},
    mdOptions = {},
  }) {
    let result = true;
    const originalModule = this.getModuleById(selectedModelId);
    const modules = this.sceneManager.cabinetManager.currentCabinet.modules;

    if (originalModule === null) return;

    const originType = originalModule.group.userData.moduleType;
    Array.from(modules).forEach(([key, module]) => {
      const id = module.group.uuid;
      const type = module.group.userData.moduleType;

      const limitWidth = module.moduleData?.limit?.width;
      const limitHeight = module.moduleData?.limit?.height;
      const limitDepth = module.moduleData?.limit?.depth;

      const {
        width: newWidth,
        height: newHeight,
        depth: newDepth,
      } = mdDimensions;
      if (newWidth < limitWidth.min || newWidth > limitWidth.max) {
        return;
      }
      if (newHeight < limitHeight.min || newHeight > limitHeight.max) {
        return;
      }
      if (newDepth < limitDepth.min || newDepth > limitDepth.max) {
        return;
      }

      // Áp dụng cho tất cả modules cùng loại, trừ module được chọn
      if (
        id !== selectedModelId &&
        ['upper', 'ceiling', 'lower'].some(
          item => type.includes(item) && originType.includes(item)
        ) &&
        !['totl_lower', 'cabinet_dry_lower'].some(item => type.includes(item))
      ) {
        if (['upper', 'ceiling'].some(item => type.includes(item))) {
          // Áp dụng fit/unfit ceiling
          if (allowFit) this.fitCeilingCabinet(insideLoaderRef, id);
          else if (allowFit === false) {
            this.unFitCeilingCabinet(insideLoaderRef, id);
          } else {
            result = this.scaleModule(
              insideLoaderRef,
              id,
              mdDimensions,
              mdOptions
            );
          }
        } else {
          // Áp dụng scaling cho các loại khác
          result = this.scaleModule(
            insideLoaderRef,
            id,
            mdDimensions,
            mdOptions
          );
        }
      }
    });
    if (result)
      openMessageSuccess({
        content: i18n.t('Successfully applied'),
      });
  }
}
